import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch } from 'helpers/store'
import LeadsAction from 'store/leads/LeadsAction'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import { Lead } from 'types/entities/Lead'
import { Offer } from 'types/entities/Offer'
import { LeadCategory } from 'types/enums/LeadCategory'
import { isLinkedOpportunityLead } from 'utils/leads/linkedOpportunityRejectionUtils'

const fetchLinkedLead = (
    dispatch: ReturnType<typeof useAppDispatch>,
    leadId: number,
    categoryHint?: string
): Promise<Lead> => {
    const fetchAction =
        categoryHint === LeadCategory.Opportunity
            ? OpportunitiesAction.fetchLeadById({ id: leadId } as Lead)
            : LeadsAction.fetchLeadById({ id: leadId } as Lead)

    return dispatch(fetchAction)
}

export const useLinkedOpportunityForOffer = (offer?: Offer) => {
    const dispatch = useAppDispatch()
    const leadId = offer?.lead_id ?? offer?.lead?.id ?? null
    const categoryHint = offer?.lead?.category
    const [linkedOpportunity, setLinkedOpportunity] = useState<Lead | null>(null)

    const refreshLinkedOpportunity = useCallback(async () => {
        if (!leadId) {
            setLinkedOpportunity(null)
            return null
        }

        try {
            const lead = await fetchLinkedLead(dispatch, leadId, categoryHint)
            if (isLinkedOpportunityLead(lead)) {
                setLinkedOpportunity(lead)
                return lead
            }

            setLinkedOpportunity(null)
            return null
        } catch {
            setLinkedOpportunity(null)
            return null
        }
    }, [dispatch, leadId, categoryHint])

    useEffect(() => {
        if (!leadId) {
            setLinkedOpportunity(null)
            return
        }

        void refreshLinkedOpportunity()
    }, [leadId, offer?.id, categoryHint, refreshLinkedOpportunity])

    return { linkedOpportunity, refreshLinkedOpportunity }
}
